// ===== Packages =====

import React                        from 'react';
import styled                       from 'styled-components';
import { ReactSVG }                 from 'react-svg';

// ===== Components =====

import Button                       from './Button';

// ===== Services =====

import {
    getPressEventHandlers,
    getTouchMouseEventHandlers,
}                                   from '../../../services';

// ===== Enums =====

import {
    BUTTON_TYPE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
}                                   from '../../../enums';

// ===== Interfaces =====

import {
    IModalMessage,
}                                   from '../../../interfaces';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    HOVER_TARGET_CLASSNAME,
}                                   from '../../../constants/generalConstants';
import FONT_TYPE                    from '../../../constants/fontType';
import CURSOR_SIGN                  from '../../../constants/cursorSigns';

// ===== Styles =====

import { theme as themeObj }        from '../../../themes/theme-context';

GenericModal.defaultProps = {
    callback: undefined,
    handleClose: undefined,
    acceptText: 'Accept',
};
interface Props {
    customCursorActive: boolean,
    message: IModalMessage,
    callback?: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    handleClose?: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    acceptText?: string,
    hideCancel: boolean,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}
function GenericModal({
    customCursorActive,
    message,
    callback,
    handleClose,
    acceptText = 'Accept',
    hideCancel = false,
    onCursorEnter,
    onCursorLeave,
}: Props): JSX.Element {
    // ===== Methods =====

    const onAcceptButtonMouseEnter = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        onCursorEnter(
            CURSOR_TARGET.modalAcceptButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onAcceptButtonMouseLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    const onCloseButtonMouseEnter = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        onCursorEnter(
            CURSOR_TARGET.modalCloseButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onCloseButtonMouseLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    return (
        <Container>
            {((
                message.icon
            && message.icon.includes('.svg')
            ) || message.title)
        && (
            <ModalHeader>
                {message.icon
                && message.icon.includes('.svg')
                && (
                    <ModalIcon>
                        <ReactSVG
                            src={message.icon}
                        />
                    </ModalIcon>
                )}
                {message.title
                && (
                    <ModalTitle>
                        {message.title}
                    </ModalTitle>
                )}
            </ModalHeader>
        )}
            <ModalText>
                {message.message}
            </ModalText>
            <ButtonRow>
                {callback
            && (
                <ButtonContainer>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        background={themeObj.verascopeColor.orange200}
                        text={acceptText}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onAcceptButtonMouseEnter,
                            onMouseLeave: onAcceptButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: (e) => {
                                if (callback) {
                                    callback(e);
                                }
                            },
                            onMouseDown: (e) => {
                                if (callback) {
                                    callback(e);
                                }
                            },
                        })}
                    />
                </ButtonContainer>
            )}
                {!hideCancel
            && (
                <ButtonContainer>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        background={themeObj.verascopeColor.purple200}
                        text="Cancel"
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onCloseButtonMouseEnter,
                            onMouseLeave: onCloseButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (handleClose) {
                                    handleClose(e);
                                }
                            },
                            onMouseDown: (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (handleClose) {
                                    handleClose(e);
                                }
                            },
                        })}
                    />
                </ButtonContainer>
            )}
            </ButtonRow>
        </Container>
    );
}

// ===== Styled Components =====

const Container = styled.div`
    margin: ${`${3.125 * BODY_FONT_SIZE}px ${1.25 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 1em;
    text-align: center;
    line-height: 1.8em;

    strong {
        display: block;
        margin: ${`${0.625 * BODY_FONT_SIZE}px`};
    }
`;

const ButtonRow = styled.div`
    width: 100%;
`;

const ButtonContainer = styled.div`
    display: inline-block;
    margin: ${`${2 * BODY_FONT_SIZE}px ${0.625 * BODY_FONT_SIZE}px 0`};
`;

const ModalHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
`;

const ModalTitle = styled.h2`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 2em;
    font-weight: 700;
    color: ${({ theme }) => theme.color.white};
    margin: 0px;
    margin-left: ${`${0.625 * BODY_FONT_SIZE}px`};
`;

const ModalIcon = styled.div`
    width: ${`${2.8125 * BODY_FONT_SIZE}px`};
    height: ${`${2.8125 * BODY_FONT_SIZE}px`};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
        fill: ${({ theme }) => theme.verascopeColor.orange200};
    }
`;

const ModalText = styled.p`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 1.1em;
    font-weight: 400;
    color: ${({ theme }) => theme.color.white};
    line-height: 1.5em;
    max-width: 60ch;
`;

export default GenericModal;
